import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { EllipsisIcon } from 'lucide-react';
import type { PrioritizedProductListEntry } from 'contracts/prioritized-products';
import { DataTableHelper, Formatter } from 'utils';
import { PriorityIndicator } from 'pages/stock-inspection/shared-components';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';

const dt = new DataTableHelper<PrioritizedProductListEntry>();

type Props = {
  onUpdatePriority?: (selectedProduct: PrioritizedProductListEntry) => void;
  onRevertPrioritizedProduct?: (selectedProduct: PrioritizedProductListEntry) => void;
  showSelectionColumn: boolean;
}

export const usePrioritizedProductColumns = ({
                                               onUpdatePriority,
                                               onRevertPrioritizedProduct,
                                               showSelectionColumn,
                                             }: Props): ColumnDef<PrioritizedProductListEntry>[] => {
  const selection = dt.buildSelectionByRowColumn();
  const priorityIndicator = dt.buildColumn({
    id: 'Prior.',
    size: 100,
    accessorKey: 'priority',
    content: (row) => <PriorityIndicator priority={row.priority} />,
  });
  const plant = dt.buildColumn({
    id: 'Planta',
    size: 120,
    accessorKey: 'plant',
    content: (row) => row.plant,
  });
  const destinationPlant = dt.buildColumn({
    id: 'Planta Destino',
    size: 120,
    accessorKey: 'destinationPlant',
    content: (row) => row.destinationPlant ?? '---',
  });
  const storageLocation = dt.buildColumn({
    id: 'SLOC',
    size: 120,
    accessorKey: 'storageLocation',
    content: (row) => row.storageLocation,
  });
  const material = dt.buildColumn({
    id: 'Código',
    size: 120,
    accessorKey: 'material',
    content: (row) => row.material,
  });
  const materialDescription = dt.buildColumn({
    id: 'Produto',
    size: 400,
    accessorKey: 'materialDescription',
    content: (row) => (
      <div
        className={'w-[210px] truncate'}
        title={row.materialDescription}
      >
        {row.materialDescription}
      </div>
    ),
  });
  const batch = dt.buildColumn({
    id: 'Lote',
    size: 120,
    accessorKey: 'batch',
    content: (row) => row.batch,
  });
  const expirationDate = dt.buildColumn({
    id: 'Dt. Venc.',
    size: 120,
    accessorKey: 'shelfLifeExpirationDate',
    content: (row) => row.shelfLifeExpirationDate
      ? Formatter.date(row.shelfLifeExpirationDate)
      : '---',
  });
  const deliveryForecastDate = dt.buildColumn({
    id: 'Dt. Prev. Recebimento',
    size: 120,
    accessorKey: 'deliveryForecastDate',
    content: (row) => row.deliveryForecastDate
      ? Formatter.date(row.deliveryForecastDate)
      : '---',
  });
  const createdAt = dt.buildColumn({
    id: 'Dt. Bloq.',
    size: 120,
    accessorKey: 'createdAt',
    content: (row) => Formatter.date(row.createdAt),
  });
  const amount = dt.buildColumn({
    id: 'Quantidate',
    size: 120,
    accessorKey: 'blockedStock',
    content: (row) => row.blockedStock || '---',
  });
  const usdValue = dt.buildColumn({
    id: 'Valor USD',
    size: 120,
    accessorKey: 'totalValue',
    content: (row) => row.totalValue
      ? Formatter.currency(row.totalValue, 'USD')
      : '---',
  });
  const estimation = dt.buildColumn<string>({
    id: 'Estimativa',
    size: 120,
    accessorKey: 'estimation',
    // TODO: calculate this on the API in a future implementation
    content: () => '---',
  });
  const reason = dt.buildColumn({
    id: 'Motivo',
    size: 400,
    accessorFn: (row) => row.productReasonType?.id,
    content: (row) => (
      <div
        className={'w-[210px] truncate'}
        title={row.productReasonType?.description}
      >
        {row.productReasonType?.description ?? '---'}
      </div>
    ),
  });
  const prioritizationComment = dt.buildColumn({
    id: 'Observação ao priorizar',
    size: 300,
    accessorFn: (row) => row.meta.prioritizationComment,
    content: (row) => {
      const { prioritizationComment } = row.meta;
      const showPopover = prioritizationComment && prioritizationComment.length > 30;
      return (
        <div className={'flex items-center'}>
          <div className={'max-w-[200px] truncate'}>
            {prioritizationComment ?? '---'}
          </div>
          {showPopover && (
            <Popover>
              <PopoverTrigger onClick={(event) => event.stopPropagation()} className={'ml-auto'}>Ver
                mais</PopoverTrigger>
              <PopoverContent>{prioritizationComment}</PopoverContent>
            </Popover>
          )}
        </div>
      );
    },
  });
  const prioritizedBy = dt.buildColumn({
    id: 'Priorizado por',
    size: 300,
    accessorFn: (row) => row.meta.prioritizedBy?.email,
    content: (row) => {
      const { prioritizedBy } = row.meta;
      if (!prioritizedBy) return '---';

      return (
        <div className={'max-w-[300px] truncate'}>
          {prioritizedBy.name}
        </div>
      );
    },
  });
  const actions = dt.buildColumn<string>({
    id: 'Actions',
    size: 48,
    title: '',
    accessorKey: 'actions',
    content: (row) => {
      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger onClick={(event) => event.stopPropagation()}>
            <EllipsisIcon className={'text-gray-600'} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align={'end'}>
            <DropdownMenuItem onClick={(event) => {
              onUpdatePriority?.(row);
              event.stopPropagation();
            }}>
              Alterar prioridade
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(event) => {
              onRevertPrioritizedProduct?.(row);
              event.stopPropagation();
            }}>
              Cancelar prioridade
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
    enableHiding: false,
    enableSorting: false,
  });

  return [
    ...(showSelectionColumn ? [selection] : []),
    priorityIndicator,
    plant,
    destinationPlant,
    storageLocation,
    material,
    materialDescription,
    batch,
    expirationDate,
    deliveryForecastDate,
    createdAt,
    amount,
    usdValue,
    estimation,
    reason,
    prioritizationComment,
    prioritizedBy,
    actions,
  ];
};